import { useState, useEffect, useMemo } from 'react';
import { getProjectPlanning, updateProjectTask } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar as CalendarIcon, BarChart3, Users, Clock, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#94a3b8' };
const STATUS_COLORS = { todo: '#94a3b8', in_progress: '#3b82f6', done: '#10b981' };
const STATUS_LABELS = { todo: 'A faire', in_progress: 'En cours', done: 'Fait' };

const ProjectPlanningTab = ({ projectId, projectColor }) => {
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('gantt');
  const [calMonth, setCalMonth] = useState(new Date());

  useEffect(() => { loadPlanning(); }, [projectId]);

  const loadPlanning = async () => {
    try {
      const r = await getProjectPlanning(projectId);
      setPlanning(r.data);
    } catch (e) { toast.error('Erreur chargement planification'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Clock className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!planning) return <div className="text-center py-12 text-slate-500">Erreur de chargement</div>;

  const views = [
    { id: 'gantt', label: 'Gantt', icon: BarChart3 },
    { id: 'calendar', label: 'Calendrier', icon: CalendarIcon },
    { id: 'workload', label: 'Charge', icon: Users },
    { id: 'hours', label: 'Heures', icon: Clock },
  ];

  return (
    <div className="space-y-4" data-testid="planning-tab">
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {views.map(v => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors ${view === v.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`planning-view-${v.id}`}>
              <Icon className="w-3.5 h-3.5" />{v.label}
            </button>
          );
        })}
      </div>

      {view === 'gantt' && <GanttView tasks={planning.tasks} milestones={planning.milestones} projectStart={planning.project_start} projectEnd={planning.project_end} projectId={projectId} onRefresh={loadPlanning} />}
      {view === 'calendar' && <CalendarView events={planning.calendar_events} month={calMonth} onMonthChange={setCalMonth} />}
      {view === 'workload' && <WorkloadView workload={planning.workload} />}
      {view === 'hours' && <HoursView tasks={planning.tasks} totalEstimated={planning.total_estimated} totalActual={planning.total_actual} untrackedHours={planning.untracked_hours} />}
    </div>
  );
};

// ============ GANTT VIEW ============
const GanttView = ({ tasks, milestones, projectStart, projectEnd, projectId, onRefresh }) => {
  const { dateRange, dayWidth, totalDays, startDate } = useMemo(() => {
    const dates = [];
    tasks.forEach(t => {
      if (t.start_date) dates.push(new Date(t.start_date));
      if (t.deadline) dates.push(new Date(t.deadline));
    });
    milestones.forEach(m => { if (m.date) dates.push(new Date(m.date)); });
    if (projectStart) dates.push(new Date(projectStart));
    if (projectEnd) dates.push(new Date(projectEnd));

    if (dates.length === 0) {
      const now = new Date();
      dates.push(new Date(now.getFullYear(), now.getMonth(), 1));
      dates.push(new Date(now.getFullYear(), now.getMonth() + 2, 0));
    }

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    minDate.setDate(minDate.getDate() - 3);
    maxDate.setDate(maxDate.getDate() + 7);

    const totalDays = Math.max(14, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));
    const dayWidth = Math.max(24, Math.min(40, 900 / totalDays));

    return { dateRange: { min: minDate, max: maxDate }, dayWidth, totalDays, startDate: minDate };
  }, [tasks, milestones, projectStart, projectEnd]);

  const dayToX = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const diff = (d - startDate) / (1000 * 60 * 60 * 24);
    return Math.round(diff * dayWidth);
  };

  const todayX = dayToX(new Date().toISOString().slice(0, 10));

  // Generate month headers
  const monthHeaders = useMemo(() => {
    const headers = [];
    const cur = new Date(startDate);
    cur.setDate(1);
    while (cur <= dateRange.max) {
      const x = dayToX(cur.toISOString().slice(0, 10));
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const endX = dayToX(next.toISOString().slice(0, 10));
      headers.push({ label: cur.toLocaleDateString('fr-CH', { month: 'short', year: '2-digit' }), x: Math.max(0, x), width: endX - Math.max(0, x) });
      cur.setMonth(cur.getMonth() + 1);
    }
    return headers;
  }, [startDate, dateRange, dayWidth]);

  const totalWidth = totalDays * dayWidth;

  const handleDependencyToggle = async (taskId, depId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const deps = task.dependencies || [];
    const newDeps = deps.includes(depId) ? deps.filter(d => d !== depId) : [...deps, depId];
    try {
      await updateProjectTask(projectId, taskId, { dependencies: newDeps });
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <Card className="border-slate-200" data-testid="gantt-view">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />Diagramme de Gantt
          <span className="text-xs text-slate-400 font-normal ml-2">({tasks.length} taches)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {tasks.length === 0 ? (
          <div className="text-center py-8 text-slate-400">Ajoutez des dates (debut/deadline) aux taches pour les voir sur le Gantt</div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: totalWidth + 240 }}>
              {/* Month headers */}
              <div className="flex border-b border-slate-200 bg-slate-50">
                <div className="w-[240px] flex-shrink-0 px-3 py-2 text-xs font-medium text-slate-500 border-r border-slate-200">Tache</div>
                <div className="relative flex-1" style={{ height: 28 }}>
                  {monthHeaders.map((mh, i) => (
                    <div key={i} className="absolute top-0 text-xs font-medium text-slate-500 px-2 py-2 border-r border-slate-100" style={{ left: mh.x, width: mh.width }}>{mh.label}</div>
                  ))}
                </div>
              </div>

              {/* Tasks */}
              {tasks.map((task, idx) => {
                const hasStart = !!task.start_date;
                const hasEnd = !!task.deadline;
                const barStart = hasStart ? dayToX(task.start_date) : (hasEnd ? dayToX(task.deadline) - dayWidth : null);
                const barEnd = hasEnd ? dayToX(task.deadline) : (hasStart ? dayToX(task.start_date) + dayWidth * 3 : null);
                const barWidth = barStart !== null && barEnd !== null ? Math.max(dayWidth, barEnd - barStart) : 0;
                const deps = task.dependencies || [];

                return (
                  <div key={task.id} className="flex border-b border-slate-50 hover:bg-slate-50/50 group" style={{ height: 36 }}>
                    <div className="w-[240px] flex-shrink-0 px-3 flex items-center gap-2 border-r border-slate-100 overflow-hidden">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[task.status] }} />
                      <span className="text-xs font-medium text-slate-700 truncate">{task.title}</span>
                      {deps.length > 0 && <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded">{deps.length} dep</span>}
                    </div>
                    <div className="relative flex-1">
                      {/* Today line */}
                      {todayX > 0 && <div className="absolute top-0 bottom-0 w-px bg-red-300 z-10" style={{ left: todayX }} />}
                      {/* Bar */}
                      {barStart !== null && (
                        <div className="absolute top-1.5 rounded-sm h-5 flex items-center px-1.5 text-[10px] text-white font-medium overflow-hidden"
                          style={{ left: barStart, width: barWidth, backgroundColor: STATUS_COLORS[task.status], opacity: task.status === 'done' ? 0.6 : 1 }}
                          data-testid={`gantt-bar-${task.id}`}>
                          {barWidth > 60 && <span className="truncate">{task.title}</span>}
                        </div>
                      )}
                      {/* No dates */}
                      {barStart === null && (
                        <div className="absolute top-2 left-4 text-[10px] text-slate-300 italic">Pas de dates</div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Milestones */}
              {milestones.filter(m => m.date).map((m, i) => (
                <div key={m.id || i} className="flex border-b border-slate-50" style={{ height: 32 }}>
                  <div className="w-[240px] flex-shrink-0 px-3 flex items-center gap-2 border-r border-slate-100">
                    <span className="text-amber-500 text-xs">&#9670;</span>
                    <span className="text-xs font-medium text-amber-700 truncate">{m.title || 'Jalon'}</span>
                  </div>
                  <div className="relative flex-1">
                    {dayToX(m.date) !== null && (
                      <div className="absolute top-1" style={{ left: dayToX(m.date) - 8 }}>
                        <div className={`w-4 h-4 rotate-45 ${m.completed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ CALENDAR VIEW ============
const CalendarView = ({ events, month, onMonthChange }) => {
  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1);
  const lastDay = new Date(year, mo + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Monday first
  const totalCells = startPad + lastDay.getDate();
  const rows = Math.ceil(totalCells / 7);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (!e.date) return;
      const key = e.date.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [events]);

  const today = new Date().toISOString().slice(0, 10);
  const days = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

  return (
    <Card className="border-slate-200" data-testid="calendar-view">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            {month.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}
          </CardTitle>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(year, mo - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date())} className="text-xs">Aujourd'hui</Button>
            <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(year, mo + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
          {days.map(d => <div key={d} className="bg-slate-50 text-center py-1.5 text-xs font-semibold text-slate-500">{d}</div>)}
          {Array.from({ length: rows * 7 }).map((_, i) => {
            const dayNum = i - startPad + 1;
            const isValid = dayNum >= 1 && dayNum <= lastDay.getDate();
            const dateStr = isValid ? `${year}-${String(mo + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}` : '';
            const dayEvents = isValid ? (eventsByDate[dateStr] || []) : [];
            const isToday = dateStr === today;

            return (
              <div key={i} className={`bg-white min-h-[80px] p-1 ${!isValid ? 'bg-slate-50/50' : ''}`}>
                {isValid && (
                  <>
                    <div className={`text-xs font-medium mb-0.5 w-5 h-5 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>
                      {dayNum}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((ev, j) => (
                        <div key={j} className="text-[10px] leading-tight px-1 py-0.5 rounded truncate"
                          style={{
                            backgroundColor: ev.type === 'milestone' ? (ev.completed ? '#d1fae5' : '#fef3c7') :
                              ev.type === 'task_start' ? '#dbeafe' : (STATUS_COLORS[ev.status] + '20'),
                            color: ev.type === 'milestone' ? (ev.completed ? '#059669' : '#d97706') : STATUS_COLORS[ev.status]
                          }}>
                          {ev.type === 'milestone' && '◆ '}{ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[9px] text-slate-400 px-1">+{dayEvents.length - 3}</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />A faire</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />En cours</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Fait</span>
          <span className="flex items-center gap-1"><span className="text-amber-500">◆</span>Jalon</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ WORKLOAD VIEW ============
const WorkloadView = ({ workload }) => {
  const maxHours = Math.max(1, ...workload.map(w => Math.max(w.estimated, w.actual)));

  return (
    <Card className="border-slate-200" data-testid="workload-view">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" />Charge de travail par personne</CardTitle>
      </CardHeader>
      <CardContent>
        {workload.length === 0 ? (
          <p className="text-center text-slate-400 py-8">Assignez des taches pour voir la charge de travail</p>
        ) : (
          <div className="space-y-4">
            {workload.map((w, i) => {
              const pctEst = (w.estimated / maxHours) * 100;
              const pctAct = (w.actual / maxHours) * 100;
              const overload = w.estimated > 0 && w.actual > w.estimated;
              return (
                <div key={i} className="space-y-1.5" data-testid={`workload-person-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-slate-600">{w.assignee[0]?.toUpperCase()}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{w.assignee}</span>
                      {overload && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                    </div>
                    <div className="text-xs text-slate-500">
                      {w.tasks_done}/{w.tasks_total} taches
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 w-14">Estime</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${pctEst}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-12 text-right">{w.estimated}h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 w-14">Reel</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${overload ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pctAct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-12 text-right">{w.actual}h</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ HOURS COMPARISON VIEW ============
const HoursView = ({ tasks, totalEstimated, totalActual, untrackedHours }) => {
  const tasksWithHours = tasks.filter(t => t.estimated_hours > 0 || t.actual_hours > 0);
  const totalAll = totalActual + untrackedHours;
  const variance = totalEstimated > 0 ? round2(((totalActual - totalEstimated) / totalEstimated) * 100) : 0;

  return (
    <Card className="border-slate-200" data-testid="hours-view">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Heures estimees vs reelles</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-[10px] text-blue-600 font-medium">Estime total</p>
            <p className="text-lg font-bold text-blue-700">{totalEstimated}h</p>
          </div>
          <div className={`rounded-lg p-3 ${variance > 10 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
            <p className={`text-[10px] font-medium ${variance > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>Reel (sur taches)</p>
            <p className={`text-lg font-bold ${variance > 10 ? 'text-amber-700' : 'text-emerald-700'}`}>{totalActual}h</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 font-medium">Non assigne</p>
            <p className="text-lg font-bold text-slate-600">{untrackedHours}h</p>
          </div>
          <div className={`rounded-lg p-3 ${Math.abs(variance) > 20 ? 'bg-red-50' : 'bg-slate-50'}`}>
            <p className="text-[10px] text-slate-500 font-medium">Ecart</p>
            <p className={`text-lg font-bold ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
              {variance > 0 ? '+' : ''}{variance}%
            </p>
          </div>
        </div>

        {/* Per-task breakdown */}
        {tasksWithHours.length > 0 ? (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase">Detail par tache</h4>
            {tasksWithHours.map(t => {
              const est = t.estimated_hours || 0;
              const act = t.actual_hours || 0;
              const maxH = Math.max(est, act, 1);
              const over = est > 0 && act > est;
              return (
                <div key={t.id} className="flex items-center gap-3 py-1.5 border-b border-slate-50">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[t.status] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{t.title}</p>
                    <div className="flex gap-1 mt-0.5 h-2">
                      <div className="bg-blue-200 rounded-sm" style={{ width: `${(est / maxH) * 50}%` }} title={`Estime: ${est}h`} />
                      <div className={`rounded-sm ${over ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${(act / maxH) * 50}%` }} title={`Reel: ${act}h`} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 w-24">
                    <span className="text-[10px] text-blue-500">{est}h</span>
                    <span className="text-[10px] text-slate-300 mx-1">/</span>
                    <span className={`text-[10px] font-medium ${over ? 'text-amber-600' : 'text-emerald-600'}`}>{act}h</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-slate-400 py-4 text-sm">Ajoutez des heures estimees aux taches pour voir la comparaison</p>
        )}

        {/* Legend */}
        <div className="flex gap-4 text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span className="flex items-center gap-1"><span className="w-3 h-1.5 bg-blue-300 rounded-sm" />Estime</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1.5 bg-emerald-400 rounded-sm" />Reel</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1.5 bg-amber-400 rounded-sm" />Depassement</span>
        </div>
      </CardContent>
    </Card>
  );
};

const round2 = (n) => Math.round(n * 10) / 10;

export default ProjectPlanningTab;
